/**
 * LES LISTES « FACTURES » ET « DEVIS » — ce qu'elles ont en commun.
 *
 * Amine, 19 septembre 2026 : les deux pages doivent avoir « exactement la
 * même grammaire visuelle » — recherche, période, filtres, synthèse, groupes
 * par mois, un seul menu par ligne. Ce fichier porte les règles partagées :
 * la période (année en cours par défaut, sans rien coder en dur), le
 * regroupement par mois et le libellé qui dit ce qu'un mois contient.
 * Aucune règle de facturation n'est réécrite ici : les montants viennent de
 * « avoirs.ts » et « rapports.ts ».
 */
import { bornesDeLaPeriode, type BornesPeriode } from '@/lib/rapports';
import { compteDansLeTotalAffiche, netDeLaFacture, estAvoir, type DocumentAvecLien } from '@/lib/avoirs';

export type PeriodeListe = 'annee' | 'annee-precedente' | 'mois' | 'mois-precedent' | '3mois' | '12mois' | 'custom';

/** L'année en cours d'abord : c'est le filtre actif à l'ouverture. */
export const PERIODES_LISTE: { valeur: PeriodeListe; libelle: (maintenant: Date) => string }[] = [
  { valeur: 'annee', libelle: (m) => `Année ${m.getFullYear()}` },
  { valeur: 'annee-precedente', libelle: (m) => `Année ${m.getFullYear() - 1}` },
  { valeur: 'mois', libelle: () => 'Ce mois' },
  { valeur: 'mois-precedent', libelle: () => 'Mois précédent' },
  { valeur: '3mois', libelle: () => '3 derniers mois' },
  { valeur: '12mois', libelle: () => '12 derniers mois' },
  { valeur: 'custom', libelle: () => 'Période personnalisée' },
];

export function libelleDePeriode(periode: PeriodeListe, maintenant: Date, customStart?: string | null, customEnd?: string | null): string {
  if (periode === 'custom' && customStart && customEnd) {
    const f = (iso: string) => {
      const d = new Date(`${iso}T00:00:00`);
      return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('fr-FR');
    };
    return `Du ${f(customStart)} au ${f(customEnd)}`;
  }
  return PERIODES_LISTE.find((p) => p.valeur === periode)?.libelle(maintenant) ?? '';
}

/**
 * Les bornes d'une période de liste. Les quatre périodes glissantes sont
 * celles des rapports — même fonction, mêmes bornes. L'année en cours et la
 * précédente sont des années civiles pleines.
 */
export function bornesDeLaListe(
  periode: PeriodeListe,
  maintenant: Date,
  customStart?: string | null,
  customEnd?: string | null
): BornesPeriode {
  switch (periode) {
    case 'annee': {
      const an = maintenant.getFullYear();
      return { debut: new Date(an, 0, 1, 0, 0, 0, 0), fin: new Date(an, 11, 31, 23, 59, 59, 999) };
    }
    case 'annee-precedente': {
      const an = maintenant.getFullYear() - 1;
      return { debut: new Date(an, 0, 1, 0, 0, 0, 0), fin: new Date(an, 11, 31, 23, 59, 59, 999) };
    }
    case 'mois': return bornesDeLaPeriode('month', maintenant);
    case 'mois-precedent': {
      const an = maintenant.getFullYear(), m = maintenant.getMonth() - 1;
      return { debut: new Date(an, m, 1, 0, 0, 0, 0), fin: new Date(an, m + 1, 0, 23, 59, 59, 999) };
    }
    case '3mois': return bornesDeLaPeriode('3months', maintenant);
    case '12mois': return bornesDeLaPeriode('rolling12', maintenant);
    case 'custom':
    default: return bornesDeLaPeriode('custom', maintenant, customStart, customEnd);
  }
}

export function dansLesBornes(dateISO: string | null | undefined, bornes: BornesPeriode): boolean {
  if (!dateISO) return false;
  const t = new Date(dateISO).getTime();
  if (Number.isNaN(t)) return false;
  return t >= bornes.debut.getTime() && t <= bornes.fin.getTime();
}

// ---------------------------------------------------------------------------
// Les groupes mensuels
// ---------------------------------------------------------------------------

export interface GroupeMensuel<T> {
  /** « 2026-09 » — la clé de tri. */
  cle: string;
  /** « Septembre 2026 ». */
  libelle: string;
  documents: T[];
}

const CLE_SANS_DATE = '0000-00';

function cleDuMois(dateISO: string | null | undefined): string {
  if (!dateISO) return CLE_SANS_DATE;
  const d = new Date(dateISO);
  if (Number.isNaN(d.getTime())) return CLE_SANS_DATE;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function libelleDuMois(cle: string): string {
  if (cle === CLE_SANS_DATE) return 'Dates invalides';
  const [an, mois] = cle.split('-').map(Number);
  const brut = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' }).format(new Date(an, mois - 1, 1));
  return brut.charAt(0).toUpperCase() + brut.slice(1);
}

/** Les documents rangés par mois d'émission, du plus récent au plus ancien ; les sans-date en dernier. */
export function grouperParMois<T extends { dateEmission: string }>(documents: T[]): GroupeMensuel<T>[] {
  const groupes = new Map<string, T[]>();
  for (const d of documents) {
    const cle = cleDuMois(d.dateEmission);
    groupes.set(cle, [...(groupes.get(cle) || []), d]);
  }
  return [...groupes.entries()]
    .sort(([a], [b]) => (a === CLE_SANS_DATE ? 1 : b === CLE_SANS_DATE ? -1 : b.localeCompare(a)))
    .map(([cle, docs]) => ({ cle, libelle: libelleDuMois(cle), documents: docs }));
}

const euros = (n: number) => n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
const arrondi = (n: number) => Math.round(n * 100) / 100;

/**
 * Ce qu'un mois de FACTURES contient, dit en clair : « 2 brouillons •
 * 47 256,00 € », « 1 facture payée • 24 144,00 € », « 2 factures •
 * 22 644,00 € ». Le montant est celui que la liste a toujours montré : net
 * des avoirs liés, annulées exclues (« compteDansLeTotalAffiche »).
 */
export function resumeDuMoisFactures(
  factures: DocumentAvecLien[],
  avoirsParFacture: Map<string, DocumentAvecLien[]>
): string {
  const comptees = factures.filter((f) => !estAvoir(f) && compteDansLeTotalAffiche(f));
  const montant = arrondi(comptees.reduce((s, f) => s + netDeLaFacture(f, avoirsParFacture.get(f.id || '') || []), 0));
  const n = comptees.length;
  if (n === 0) return factures.length > 0 ? `${factures.length} annulée${factures.length > 1 ? 's' : ''}` : '';
  const tousBrouillons = comptees.every((f) => f.statut === 'Brouillon');
  const toutesPayees = comptees.every((f) => f.statut === 'Payée');
  const nom = tousBrouillons
    ? `brouillon${n > 1 ? 's' : ''}`
    : toutesPayees
      ? `facture${n > 1 ? 's' : ''} payée${n > 1 ? 's' : ''}`
      : `facture${n > 1 ? 's' : ''}`;
  return `${n} ${nom} • ${euros(montant)}`;
}

/** « 4 devis • 8 420,00 € ». */
export function resumeDuMoisDevis(devis: { totalTTC: number }[]): string {
  const montant = arrondi(devis.reduce((s, d) => s + (Number(d.totalTTC) || 0), 0));
  return `${devis.length} devis • ${euros(montant)}`;
}
